import { useNotification, useStatusBar } from "@ui-kit";
import { useLogging } from "@src/hooks";
import { SessionStorageService, ConfigService } from "@src/services";

type BaseApiOption<B> = {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  body?: B;
  skipNotifyUnhandledError?: boolean;
};

export type BaseApiError = {
  code: number;
  handled: true;
  message: string;
};

// @ts-expect-error - VITE_SERVER_URL is defined in environment
const domain = import.meta.env.VITE_SERVER_URL;

export const baseApi = async <R, B = undefined>({
  url,
  method = "GET",
  body,
  ...options
}: BaseApiOption<B>): Promise<R> => {
  const { setServerStatus } = useStatusBar();
  const { show } = useNotification();
  const { addLog } = useLogging();

  const token = SessionStorageService.getItem("token");
  const currentLanguage = ConfigService.getLanguage() || "ru";
  const headers: Record<string, string> = { "content-type": "application/json" };

  if (token) headers.authorization = `Bearer ${token}`;

  const timeoutId = setTimeout(() => setServerStatus("error"), 5000);

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  addLog({
    url: "SEND:" + url,
    status: "success",
    timestamp: new Date().toISOString(),
    data: body,
  });

  setServerStatus("success");

  try {
    const response = await fetch(`${domain}${url}`, {
      method,
      body: body && JSON.stringify(body),
      headers,
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    addLog({
      url: "RESPONSE:" + url,
      status: response.ok ? "success" : "error",
      timestamp: new Date().toISOString(),
      data,
    });

    if (response.ok) {
      return data as R;
    } else {
      const message = currentLanguage === "ru" ? data.ru_message || data.message : data.message;

      switch (response.status) {
        case 500: {
          show({ title: message || "Server error", type: "error" });
          break;
        }
        case 401: {
          SessionStorageService.removeItem("token");

          show({ title: message || "Authorization error", type: "error" });

          if (window.location.pathname !== "/login") {
            window.location.reload();
          }
          break;
        }
        case 403: {
          show({ title: message || "Access denied", type: "error" });
          break;
        }
      }

      throw { code: response.status, handled: true, message: data?.ru_message || "" };
    }
  } catch (error) {
    if (!error?.handled) {
      addLog({
        url,
        status: "error",
        timestamp: new Date().toISOString(),
        data: error,
        isString: true,
      });

      if (!options.skipNotifyUnhandledError) {
        show({ title: "Unhandled error", type: "error" });
      }
    }

    setServerStatus("error");

    return Promise.reject(error);
  }
};
